import getCellValue from './get-cell-value';

/**
 * Given an API response from the Google Sheets Tab endpoint, converts the
 * contents of a Sheets tab into a data structure containing configuration data
 * (e.g., population, GDP deflators, GDP) and data points for the tab.
 * @param {object} response - The response object from the API, which should
 *    contain a single key called `data` that contains an array of arrays.
 * @returns {object} - An object with config and data keys containing formatted data.
 */
const transformServiceType = (response) => {
  const compiled = {
    config: [],
    data: [],
  };

  // If the response isn't an object with a data key that is an array, bail out.
  const { data = [] } = response || {};
  if (!Array.isArray(data)) {
    return compiled;
  }

  // Loop through data and categorize each.
  const headerMap = new Map();
  const columnMap = new Map();
  return data.reduce((acc, item, index) => {
    // If we're on the first row, process headers.
    if (index === 0) {
      item.forEach((cell, cellIndex) => {
        headerMap.set(cellIndex, cell);
        columnMap.set(cell, cellIndex);
      });
      return acc;
    }

    // Determine category, subcategory, and subdivision.
    let category = index === 1 ? null : getCellValue(item, columnMap.get('Source Group'));
    if (category) {
      const categoryCompare = category.toLowerCase();
      if (categoryCompare === 'gdp'
        || categoryCompare === 'pce price index'
        || categoryCompare === 'population'
      ) {
        category = categoryCompare.replaceAll(' ', '_');
      }
    }
    const subcategory = getCellValue(item, columnMap.get('Source'));
    const subdivision = getCellValue(item, columnMap.get('Sub'));

    // Loop over cells in the row and process year cells into data points.
    item.forEach((cell, cellIndex) => {
      // Get column header for this cell.
      const header = headerMap.get(cellIndex);
      if (header.match(/^[0-9]+$/)) {
        const value = getCellValue(item, cellIndex);
        const year = parseInt(header, 10);

        // Fork for category. Special treatment for population and GDP numbers.
        switch (category) {
          case 'gdp':
          case 'pce_price_index':
          case 'population':
            acc.config.push({
              category,
              subcategory,
              subdivision,
              value,
              year,
            });
            break;
          default:
            acc.data.push({
              category,
              subcategory,
              subdivision,
              value,
              year,
            });
            break;
        }
      }
    });

    return acc;
  }, compiled);
};

export default transformServiceType;
